/**
 * @fileoverview Shared delivery-schedule manager — lists, creates, toggles, and
 *             deletes report/dashboard schedules with parameter bindings.
 * @module ui-admin/components/ScheduleManager
 *
 * Responsibilities: - Render the schedule list (preset, output, recipients,
 *                     next-run, last-run status) with expand/collapse rows.
 *                   - New-schedule form: name, preset, time, format, email
 *                     subject, validated recipients, and — when the report
 *                     declares parameters — a ParameterBindingSection.
 * Dependencies:     - React, lucide-react, ../ui/ConfirmActionModal,
 *                     ../ValidatedEmailInput, ../../utils/emailValidation,
 *                     ../ParameterEditor (ParameterBindingSection),
 *                     @firesquirrel/local-db (types), @firesquirrel/core-domain
 *                     (parameter types).
 * Architecture:     - Presentation-only with an INJECTED service port
 *                     (ScheduleServicePort) — tenant-web adapts its
 *                     reportScheduleService, system-web its
 *                     systemReportScheduleService; host wiring is never
 *                     duplicated in this component (DRY).
 * Notes:            - Recipients are validated via the shared email validator.
 *                     The port keeps schedule persistence local-first (Dexie).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, Play, Pause, Trash2, Plus, Mail, Users, Calendar,
  ChevronDown, ChevronUp, Check, X,
} from 'lucide-react';
import type { ReportScheduleLocal, ReportParameter, ParameterBinding } from '@firesquirrel/local-db';
import ConfirmActionModal from '../ui/ConfirmActionModal';
import ValidatedEmailInput from '../ValidatedEmailInput';
import { validateEmailFormat } from '../../utils/emailValidation';
import { ParameterBindingSection } from '../ParameterEditor/ParameterBindingSection';

// ---------------------------------------------------------------------------
// Service port — injected host adapter (tenant / system schedule services)
// ---------------------------------------------------------------------------

/**
 * @contract ScheduleServicePort
 * Purpose: Minimal schedule persistence surface this component needs; each host
 *   supplies a thin adapter over its own local-first schedule service.
 * Fields:
 * - listSchedules: schedules for a report definition.
 * - createSchedule: persist a new schedule (host validates bindings).
 * - deleteSchedule: tombstone/delete a schedule.
 * - setScheduleActive: pause/resume a schedule.
 * Rules: All implementations MUST be local-first (Dexie write + audit + sync);
 *   the component never talks to an API.
 */
export interface ScheduleServicePort {
  listSchedules(reportDefinitionId: string): Promise<ReportScheduleLocal[]>;
  createSchedule(input: Partial<ReportScheduleLocal>): Promise<ReportScheduleLocal>;
  deleteSchedule(id: string): Promise<unknown>;
  setScheduleActive(id: string, isActive: boolean): Promise<unknown>;
}

/**
 * @contract ScheduleManagerProps
 * Purpose: Props for the shared schedule manager.
 * Fields:
 * - reportDefinitionId / reportName: the report being scheduled.
 * - service: injected host schedule port.
 * - parameters: declared report parameters — when non-empty the new-schedule
 *   form renders a ParameterBindingSection.
 * - scopeLevel: schedule scope default ('department' | 'system').
 */
export interface ScheduleManagerProps {
  reportDefinitionId: string;
  reportName: string;
  service: ScheduleServicePort;
  parameters?: ReportParameter[];
  scopeLevel?: 'department' | 'system';
}

export type SchedulePreset =
  | 'daily-morning'
  | 'daily-evening'
  | 'twice-daily'
  | 'weekly-monday'
  | 'weekly-friday'
  | 'biweekly'
  | 'monthly-1st'
  | 'monthly-15th'
  | 'quarterly'
  | 'semi-annual'
  | 'yearly';

/**
 * @reusable PRESET_LABELS — human-readable cadence label per schedule preset.
 * Rules: shared by the manager form, row metadata, and grid ScheduleBadge hosts.
 */
export const PRESET_LABELS: Record<SchedulePreset, string> = {
  'daily-morning': 'Daily (Morning)',
  'daily-evening': 'Daily (Evening)',
  'twice-daily': 'Twice Daily',
  'weekly-monday': 'Weekly (Monday)',
  'weekly-friday': 'Weekly (Friday)',
  biweekly: 'Biweekly',
  'monthly-1st': 'Monthly (1st)',
  'monthly-15th': 'Monthly (15th)',
  quarterly: 'Quarterly',
  'semi-annual': 'Semi-Annual',
  yearly: 'Yearly',
};

/**
 * @reusable presetLabel — time-aware cadence label. For `daily-morning` /
 * `daily-evening` presets the parenthetical derives from the configured
 * `time` (HH:mm), not the preset key — a 19:00 schedule is not "Morning".
 * Other presets return their fixed label.
 * Rules: hour < 12 → Morning, 12–16 → Afternoon, ≥ 17 → Evening.
 */
export function presetLabel(preset: SchedulePreset | string | undefined, time?: string): string {
  const base = preset ? (PRESET_LABELS[preset as SchedulePreset] ?? 'Custom') : 'Custom';
  if ((preset === 'daily-morning' || preset === 'daily-evening') && time) {
    const hour = Number.parseInt(time.split(':')[0] ?? '', 10);
    if (!Number.isNaN(hour)) {
      const period = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
      return `Daily (${period})`;
    }
  }
  return base;
}

const FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'xlsx', label: 'Excel' },
  { value: 'csv', label: 'CSV' },
  { value: 'html', label: 'HTML' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component ScheduleManager
 * Purpose:     List and maintain delivery schedules for a report definition.
 * Props:       ScheduleManagerProps (service port injected by the host).
 * Behavior:    Loads schedules on mount; rows expand to show output/recipient/
 *              last-run detail; toggle button pauses/activates; delete confirms
 *              via ConfirmActionModal. New-schedule form validates recipients
 *              and — for parameterized reports — collects parameter bindings.
 * Data Source: local (via the injected local-first service port).
 * Constraints: no direct API calls; persistence delegated to the port.
 */
export function ScheduleManager({
  reportDefinitionId, reportName, service, parameters = [], scopeLevel = 'department',
}: ScheduleManagerProps) {
  const [schedules, setSchedules] = useState<ReportScheduleLocal[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [deleteScheduleId, setDeleteScheduleId] = useState<string | null>(null);

  const loadSchedules = useCallback(async () => {
    try {
      const data = await service.listSchedules(reportDefinitionId);
      setSchedules(data);
    } catch (err) {
      console.error('[ScheduleManager] Failed to load schedules:', err);
    } finally {
      setLoading(false);
    }
  }, [service, reportDefinitionId]);

  useEffect(() => {
    void loadSchedules();
  }, [loadSchedules]);

  const handleToggleActive = async (schedule: ReportScheduleLocal) => {
    await service.setScheduleActive(schedule._id, !schedule.isActive);
    void loadSchedules();
  };

  const doDeleteSchedule = async () => {
    if (!deleteScheduleId) return;
    await service.deleteSchedule(deleteScheduleId);
    setDeleteScheduleId(null);
    void loadSchedules();
  };

  if (loading) {
    return <div className="p-4 text-sm text-gray-500">Loading schedules...</div>;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white" data-testid="schedule-manager">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Scheduled Delivery</h3>
          <span className="text-xs text-gray-500">({schedules.length})</span>
        </div>
        <button
          type="button"
          data-testid="add-schedule"
          onClick={() => setShowNewForm(true)}
          className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--brand-primary, #2563eb)' }}
        >
          <Plus className="h-4 w-4" />
          Add Schedule
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {schedules.length === 0 && !showNewForm && (
          <div className="p-6 text-center text-gray-500">
            <Calendar className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            <p className="text-sm">No schedules configured</p>
            <p className="mt-1 text-xs text-gray-400">Add a schedule to automatically deliver this report</p>
          </div>
        )}

        {schedules.map((schedule) => (
          <ScheduleRow
            key={schedule._id}
            schedule={schedule}
            expanded={expanded === schedule._id}
            onToggleExpand={() => setExpanded(expanded === schedule._id ? null : schedule._id)}
            onToggleActive={() => void handleToggleActive(schedule)}
            onDelete={() => setDeleteScheduleId(schedule._id)}
          />
        ))}

        {showNewForm && (
          <NewScheduleForm
            reportDefinitionId={reportDefinitionId}
            reportName={reportName}
            parameters={parameters}
            scopeLevel={scopeLevel}
            service={service}
            onSave={() => { setShowNewForm(false); void loadSchedules(); }}
            onCancel={() => setShowNewForm(false)}
          />
        )}
      </div>

      {deleteScheduleId && (
        <ConfirmActionModal
          title="Delete Schedule?"
          description="This will permanently delete the schedule. This cannot be undone."
          confirmButtonLabel="Delete Schedule"
          variant="danger"
          onConfirm={doDeleteSchedule}
          onClose={() => setDeleteScheduleId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule Row
// ---------------------------------------------------------------------------

interface ScheduleRowProps {
  schedule: ReportScheduleLocal;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

function ScheduleRow({ schedule, expanded, onToggleExpand, onToggleActive, onDelete }: ScheduleRowProps) {
  const cadence = presetLabel(schedule.schedule?.preset, schedule.schedule?.time);
  const recipientCount =
    (schedule.recipients?.internal?.length ?? 0) +
    (schedule.recipients?.external?.length ?? 0);
  const bindingCount = schedule.parameterBindings?.length ?? 0;

  return (
    <div className="bg-white">
      <div className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50" onClick={onToggleExpand}>
        <button
          type="button"
          aria-label={schedule.isActive ? 'Pause schedule' : 'Activate schedule'}
          onClick={(e) => { e.stopPropagation(); onToggleActive(); }}
          className={`rounded-full p-1.5 transition-colors ${
            schedule.isActive ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
        >
          {schedule.isActive ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-gray-900">{schedule.name}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${schedule.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {schedule.isActive ? 'Active' : 'Paused'}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
            <span>{cadence}</span>
            <span>•</span>
            <span>{schedule.output?.format?.toUpperCase() ?? 'PDF'}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
            </span>
            {bindingCount > 0 && (
              <span className="rounded-sm border border-violet-200 px-1.5 py-px text-[10px] text-violet-700">
                {bindingCount} param binding{bindingCount !== 1 ? 's' : ''}
              </span>
            )}
            {schedule.nextRunAt && (
              <span className="text-gray-400">Next: {new Date(schedule.nextRunAt).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label="Delete schedule"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="rounded-sm p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 pb-4 pt-2">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-gray-500">Schedule</p>
              <p className="text-gray-900">{cadence}</p>
              <p className="text-xs text-gray-500">Time: {schedule.schedule?.time ?? '08:00'} ({schedule.schedule?.timezone})</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-gray-500">Output</p>
              <p className="text-gray-900">{schedule.output?.format?.toUpperCase()}</p>
              <p className="text-xs text-gray-500">{schedule.output?.paperSize} / {schedule.output?.orientation}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-gray-500">Recipients</p>
              {schedule.recipients?.internal?.map((r) => (
                <p key={r.userId} className="text-xs text-gray-900">{r.name} ({r.email})</p>
              ))}
              {schedule.recipients?.external?.map((r, i) => (
                <p key={i} className="text-xs text-gray-900">{r.name ?? r.email}</p>
              ))}
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-gray-500">Last Run</p>
              {schedule.lastRunAt ? (
                <>
                  <p className="text-gray-900">{new Date(schedule.lastRunAt).toLocaleString()}</p>
                  <p className={`text-xs ${schedule.lastRunStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {schedule.lastRunStatus === 'success' ? 'Successful' : 'Failed'}
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-500">Never run</p>
              )}
            </div>
            {(schedule.parameterBindings?.length ?? 0) > 0 && (
              <div>
                <p className="mb-1 text-xs font-medium uppercase text-gray-500">Parameter Bindings</p>
                {schedule.parameterBindings!.map((b) => (
                  <p key={b.parameterId} className="text-xs text-gray-900">
                    {b.parameterId}: {b.mode === 'relative' ? `⟳ ${b.token}` : String(b.value)}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Schedule Form
// ---------------------------------------------------------------------------

interface NewScheduleFormProps {
  reportDefinitionId: string;
  reportName: string;
  parameters: ReportParameter[];
  scopeLevel: 'department' | 'system';
  service: ScheduleServicePort;
  onSave: () => void;
  onCancel: () => void;
}

function NewScheduleForm({
  reportDefinitionId, reportName, parameters, scopeLevel, service, onSave, onCancel,
}: NewScheduleFormProps) {
  const [name, setName] = useState(`${reportName} - Daily`);
  const [preset, setPreset] = useState<SchedulePreset>('daily-morning');
  const [time, setTime] = useState('08:00');
  const [format, setFormat] = useState<'pdf' | 'xlsx' | 'csv' | 'html'>('pdf');
  const [emailSubject, setEmailSubject] = useState(`${reportName} Report`);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipients, setRecipients] = useState<Array<{ email: string; name?: string }>>([]);
  const [bindings, setBindings] = useState<ParameterBinding[]>([]);
  const [saving, setSaving] = useState(false);

  const handleAddRecipient = () => {
    if (!recipientEmail.trim() || !validateEmailFormat(recipientEmail.trim())) return;
    setRecipients([...recipients, { email: recipientEmail.trim() }]);
    setRecipientEmail('');
  };

  const missingRequired = parameters.filter(
    (p) => p.required && !bindings.some((b) => b.parameterId === p.parameterId),
  );

  const handleSave = async () => {
    if (!name.trim() || missingRequired.length > 0) return;
    setSaving(true);
    try {
      await service.createSchedule({
        reportDefinitionId,
        name: name.trim(),
        isActive: true,
        schedule: { type: 'preset', preset, time, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        output: { format, includeCharts: true, paperSize: 'letter', orientation: 'portrait' },
        recipients: { internal: [], external: recipients, externalGroups: [] },
        delivery: { method: 'email', emailSubject, emailBody: `Please find attached your scheduled ${reportName} report.` },
        scope: { level: scopeLevel },
        parameterBindings: bindings,
        boundParameterIds: bindings.map((b) => b.parameterId),
      } as Partial<ReportScheduleLocal>);
      onSave();
    } catch (err) {
      console.error('[ScheduleManager] Failed to create schedule:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-blue-100 bg-blue-50 p-4">
      <div className="mb-4 flex items-center gap-2">
        <Plus className="h-5 w-5 text-blue-600" />
        <h4 className="font-medium text-blue-900">New Schedule</h4>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Schedule Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} aria-label="Schedule name"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Frequency</label>
          <select value={preset} onChange={(e) => setPreset(e.target.value as SchedulePreset)} aria-label="Frequency"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500">
            {Object.entries(PRESET_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Time</label>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Time"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Output Format</label>
          <select value={format} onChange={(e) => setFormat(e.target.value as typeof format)} aria-label="Output format"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500">
            {FORMAT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="mb-1 block text-xs font-medium text-gray-700">Email Subject</label>
          <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} aria-label="Email subject"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="col-span-2">
          <label className="mb-1 block text-xs font-medium text-gray-700">Recipients</label>
          <div className="mb-2 flex items-start gap-2">
            <div className="flex-1">
              <ValidatedEmailInput
                value={recipientEmail}
                onChange={(v) => setRecipientEmail(v)}
                placeholder="email@example.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="button" onClick={handleAddRecipient}
              disabled={!validateEmailFormat(recipientEmail.trim())}
              className="mt-0 rounded-lg border border-blue-300 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40">
              Add
            </button>
          </div>
          {recipients.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {recipients.map((r, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs">
                  <Mail className="h-3 w-3 text-gray-400" />
                  {r.email}
                  <button type="button" aria-label={`Remove ${r.email}`} onClick={() => setRecipients(recipients.filter((_, j) => j !== i))}
                    className="text-gray-400 hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {parameters.length > 0 && (
          <div className="col-span-2">
            <ParameterBindingSection
              parameters={parameters}
              bindings={bindings}
              onChange={setBindings}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-blue-100 pt-4">
        <button type="button" onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="button" onClick={() => void handleSave()} disabled={saving || !name.trim() || missingRequired.length > 0}
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: 'var(--brand-primary, #2563eb)' }}>
          {saving ? 'Saving...' : (<><Check className="h-4 w-4" /> Create Schedule</>)}
        </button>
      </div>
    </div>
  );
}
